import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CardHeader,
  FormControlLabel,
  Grid,
  Loading,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
} from "../../../components";
import { useImportContext } from "../../../contexts/import.context";
import { InputErros, PermisoesHelper } from "../../../helpers";
import { FlexLayout } from "../../../layouts";
import { EmailPersonalizadoService, notification } from "../../../services";
import { responseErros, userLogs, voltar } from "../../../utils";
import { contextos } from "../resources";
import { getVariaveisPorContexto } from "../../mensagens-personalizadas/resources/constantes";
import { previewMensagem } from "../../mensagens-personalizadas/resources/mensagem-personalizada";
import emailPersonalizadoValidator from "../middlewares/email-personalizado.validator";
import { EmailPersonalizado } from "../entities";

const emailPersonalizadoService = new EmailPersonalizadoService();
const permissoesHelper = new PermisoesHelper();

let emailPersonalizadoEntity = {
  contexto: null,
  ativado: true,
};

const AddEmailPersonalizadoView = ({ match }) => {
  const id = match.params.id;
  const { initialCollapses } = useImportContext();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(emailPersonalizadoEntity);
  const [userLog, setUserLog] = useState({});
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const [listaContextosCadastrados, setListaContextosCadastrados] = useState(
    [],
  );
  permissoesHelper.validarPermisao("email-personalizado-visualizar");

  const baseContexto = email?.contexto || "";
  const emailPreview = () =>
    email?.mensagem
      ? previewMensagem(email.mensagem, baseContexto).replace(
          /<\/?(strong|em)>/g,
          "",
        )
      : "";

  const contextosShowFiltrados = contextos.filter(
    (contexto) =>
      !listaContextosCadastrados?.includes(contexto.value) ||
      contexto.value === baseContexto,
  );

  useEffect(() => {
    buscarMensagemPersonalizada();
    buscarMensagensPersonalizadas();
  }, []);

  async function buscarMensagemPersonalizada() {
    setLoading(true);
    if (id) {
      const res = await emailPersonalizadoService.getById(id);
      if (!res.isAxiosError) {
        if (!res.data) {
          initialCollapses();
          voltar();
          return;
        }
        emailPersonalizadoEntity = new EmailPersonalizado(res.data);
        setEmail({ ...emailPersonalizadoEntity });
      } else {
        responseErros(res);
      }
    } else {
      emailPersonalizadoEntity = new EmailPersonalizado({
        contexto: null,
        tipo: "SIMPLES",
        ativado: true,
      });
      setEmail({ ...emailPersonalizadoEntity });
    }
    setLoading(false);
  }

  const buscarMensagensPersonalizadas = async (filtros, listaIds) => {
    let filtro = { ...filtros, nonPaginated: true };
    if (listaIds?.length) {
      filtro = {
        ...filtro,
        id: listaIds,
        nonPaginated: true,
      };
    } else {
      filtro = { ...filtro, ...filtros, limite: 15 };
    }
    const result = await emailPersonalizadoService.getAll(filtro);
    if (!result.isAxiosError) {
      setListaContextosCadastrados(result.data.map(({ contexto }) => contexto));
    } else {
      responseErros(result);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    emailPersonalizadoEntity.setHandleChangeInput(name, value);
    setEmail({ ...emailPersonalizadoEntity });
  };

  const inserirVariavel = (variavel) => {
    const textarea = document.getElementById("mensagem");
    if (textarea) {
      const startPos = textarea.selectionStart;
      const endPos = textarea.selectionEnd;
      const variableText = startPos > 0 ? ` {${variavel}} ` : `{${variavel}} `;
      const mensagemAtual = emailPersonalizadoEntity.mensagem;
      emailPersonalizadoEntity.setHandleChangeInput(
        "mensagem",
        mensagemAtual
          ? mensagemAtual.substring(0, startPos) +
              variableText +
              mensagemAtual.substring(endPos)
          : variableText,
      );
      setEmail({ ...emailPersonalizadoEntity });

      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = startPos + variableText.length;
        textarea.selectionEnd = startPos + variableText.length;
      }, 300);
    } else {
      emailPersonalizadoEntity.setHandleChangeInput(
        "mensagem",
        emailPersonalizadoEntity.mensagem + `{${variavel}}`,
      );
      setEmail({ ...emailPersonalizadoEntity });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    await extrairVariaveis();
    emailPersonalizadoValidator
      .validate(emailPersonalizadoEntity, { abortEarly: false })
      .then(async () => {
        await createUpdateMensagem();
      })
      .catch((err) => {
        setLoading(false);
        inputErros.set(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  async function extrairVariaveis() {
    const regex = /\{([^}]+)\}/g;
    const variaveis = [];
    let match;
    const variaveisContexto = getVariaveisPorContexto(email.contexto);
    while ((match = regex.exec(email.mensagem)) !== null) {
      if (variaveisContexto.includes(match[1])) {
        variaveis.push(match[1]);
      }
    }

    emailPersonalizadoEntity.setVariaveis(variaveis);
  }

  async function createUpdateMensagem() {
    setLoading(true);
    if (id) {
      const response = await emailPersonalizadoService.atualizar(
        id,
        emailPersonalizadoEntity,
      );
      setLoading(false);
      if (!response.isAxiosError) {
        notification.alteracaoSucesso();
        voltar();
      } else {
        responseErros(response);
      }
    } else {
      const response = await emailPersonalizadoService.cadastrar(
        emailPersonalizadoEntity,
      );
      setLoading(false);
      if (!response.isAxiosError) {
        notification.cadastroSucesso();
        voltar();
      } else {
        responseErros(response);
      }
    }
  }

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(email);
    setUserLog(logs);
  }

  return (
    <FlexLayout
      id={id}
      title={`${id ? "Editar" : "Cadastrar"} Email Personalizado`}
      onClickSalvar={handleSubmit}
      loading={loading}
      userLog={id ? userLog : null}
      onClickLog={onClickLog}
      showUpdate={permissoesHelper.temPermisao("email-personalizado-editar")}
      contentWrapperClassName="flex-layout-content-fill"
    >
      <Grid container spacing={2} sx={{ px: 2 }}>
        <Grid item xs={12}>
          <Paper
            className="d-flex"
            sx={{
              px: 3,
              py: 2,
              height: "100%",
              boxShadow: "0px 0px 56px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Grid item xs={12} md={5}>
              <CardHeader title="configuração" />
              <div className="d-flex flex-column align-items-center">
                <Select
                  fullWidth
                  name="contexto"
                  value={baseContexto}
                  onChange={handleChange}
                  label="Contexto da Mensagem"
                  disabled={id}
                  sx={{ mt: 1 }}
                  error={inputErros.get("contexto")}
                >
                  {contextosShowFiltrados.map((status, index) => (
                    <MenuItem key={index} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <Box sx={{ mt: 2 }}>
                <CardHeader title="variáveis disponíveis" />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {getVariaveisPorContexto(baseContexto).map(
                    (variavel, index) => (
                      <Button
                        id={`var-${index + 1}`}
                        key={variavel}
                        variant="outlined"
                        size="small"
                        onClick={() => inserirVariavel(variavel)}
                        sx={{
                          borderRadius: "16px",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          padding: "4px 12px",
                          borderColor: "#ddd",
                          color: "#555",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            borderColor: "#ccc",
                          },
                        }}
                      >
                        {`{${variavel}}`}
                      </Button>
                    ),
                  )}
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <CardHeader title="Assunto do Email" />
                <TextField
                  id="assunto"
                  name="assunto"
                  label="Assunto"
                  margin="normal"
                  fullWidth
                  required
                  sx={{ flexGrow: 1 }}
                  onChange={handleChange}
                  value={email?.assunto}
                  error={inputErros.get("assunto")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={1} className="d-flex justify-content-center">
              <hr className="vr" />
            </Grid>

            <Grid item xs={12} md={6.5}>
              <CardHeader
                title="Corpo do email"
                sx={{
                  position: "relative",
                  "& .MuiCardHeader-action": {
                    position: "absolute",
                    top: "50%",
                    right: 16,
                    margin: 0,
                    transform: "translateY(-50%)",
                  },
                }}
                action={
                  <FormControlLabel
                    control={
                      <Switch
                        checked={email?.ativado ?? false}
                        onChange={(event) => {
                          emailPersonalizadoEntity.setHandleChangeInput(
                            "ativado",
                            event.target.checked,
                          );
                          setEmail({ ...emailPersonalizadoEntity });
                        }}
                        name="ativado"
                        color="primary"
                      />
                    }
                    label={email?.ativado ? "Ativado" : "Desativado"}
                  />
                }
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TextField
                  id="mensagem"
                  name="mensagem"
                  label="Texto da Mensagem"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={8}
                  required
                  sx={{ flexGrow: 1 }}
                  onChange={handleChange}
                  value={email?.mensagem}
                  error={inputErros.get("mensagem")}
                />
                <TextField
                  id="email-preview"
                  label="Prévia do e-mail"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={8}
                  InputProps={{ readOnly: true }}
                  value={emailPreview()}
                  placeholder="A prévia do e-mail será exibida aqui..."
                />
              </Box>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Loading loading={loading} />
    </FlexLayout>
  );
};

export default AddEmailPersonalizadoView;
